import { Link } from 'react-router-dom';
import styles from './RankedTitleCard.module.css';

const POSTER_CDN = 'https://cdn-fastly.parrotanalytics.com/images/poster-hyphen';

export interface RankedTitleCardProps {
  rank: number;
  parrotId: string;
  title: string;
  releaseYear?: number;
  genre?: string;
  contentType?: string;
  revenueUsd: number;
  /** Fraction of the platform's revenue, e.g. 0.014786. Rendered beside revenue. */
  revenueShare?: number;
  trendLabel?: string;
  trendDirection?: 'up' | 'down' | 'flat';
}

function formatRevenue(value: number): { amount: string; suffix: string } {
  if (value >= 1_000_000_000) {
    return { amount: (value / 1_000_000_000).toFixed(1), suffix: 'B' };
  }
  if (value >= 1_000_000) {
    return { amount: (value / 1_000_000).toFixed(0), suffix: 'M' };
  }
  return { amount: (value / 1_000).toFixed(0), suffix: 'K' };
}

export function RankedTitleCard({
  rank,
  parrotId,
  title,
  releaseYear,
  genre,
  contentType,
  revenueUsd,
  revenueShare,
  trendLabel,
  trendDirection = 'up',
}: RankedTitleCardProps) {
  const rev = formatRevenue(revenueUsd);
  // Only ever show metadata we actually have — no invented genres.
  const displayMeta = [contentType, genre].filter(Boolean).join(' · ');
  const posterUrl = `${POSTER_CDN}/${parrotId}.jpg?width=300`;

  return (
    <div className={styles.cardWrapper}>
      <span className={styles.rankBg}>{rank}</span>
      <Link to={`/titles/${parrotId}`} className={styles.card}>
        {/* Blurred poster behind the card, with a gradient so the content stays
            readable. Same image as the poster; hidden if it fails to load. */}
        <div
          className={styles.splashBg}
          style={{ backgroundImage: `url(${posterUrl})` }}
          aria-hidden="true"
        />
        <div className={styles.splashGrad} aria-hidden="true" />
        <div className={styles.poster}>
          <img
            src={posterUrl}
            alt={title}
            className={styles.posterImg}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className={styles.posterFallback}>
            {title.charAt(0)}
          </div>
          <button className={styles.addBtn} onClick={(e) => e.preventDefault()}>+</button>
        </div>
        <div className={styles.info}>
          <div className={styles.titleBlock}>
            {releaseYear && <span className={styles.year}>{releaseYear}</span>}
            <h3 className={styles.cardTitle}>{title}</h3>
            {displayMeta && <p className={styles.genre}>{displayMeta}</p>}
          </div>
          <div className={styles.divider} />
          <div className={styles.revenueRow}>
            <span className={styles.revenue}>
              <span className={styles.currencySign}>$</span>
              <span className={styles.revenueAmount}>{rev.amount}{rev.suffix}</span>
            </span>

            {revenueShare != null && (
              <span className={styles.shareBlock}>
                <span className={styles.shareValue}>{(revenueShare * 100).toFixed(2)}%</span>
                {/* Share is measured against the platform's revenue for this
                    content type, not its revenue overall — say so. */}
                <span className={styles.shareLabel}>
                  {contentType ? `of platform ${contentType} revenue` : 'of platform revenue'}
                </span>
              </span>
            )}

            {trendLabel && (
              <div className={styles.trendSection}>
                <span className={styles.trendLine}>
                  <span className={styles.trendIcon}>
                    {trendDirection === 'up' ? '▲' : trendDirection === 'down' ? '▼' : '—'}
                  </span>
                  <span className={styles.trendUp}>{trendLabel.split(' ')[0]}</span>
                  <span className={styles.trendText}>&nbsp;{trendLabel.split(' ').slice(1).join(' ')}</span>
                </span>
                <span className={styles.trendBadge}>
                  {trendDirection === 'up' ? 'positive' : trendDirection === 'down' ? 'negative' : 'flat'}
                </span>
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}
